import { useState, type ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AGE_GROUPS,
  CLASSIFICATIONS,
  compareSeasons,
  formatSeason,
  seasonFromValue,
  seasonOptions,
  seasonToValue,
  teamAttrs,
  teamLabel,
} from "@alpha/shared";
import { api, apiErrorMessage } from "../lib/api";
import { initials } from "../lib/format";
import { useAuth } from "../lib/auth";
import { emptyDraft, toBody, toDraft, type TeamDraft } from "../lib/teamDraft";
import { trackTeamCreated } from "../lib/analytics";
import type { Team } from "../lib/types";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  LoadingBlock,
  MapPinIcon,
  PageHeader,
  PlusIcon,
  Section,
  SectionCard,
  Select,
  SignInCard,
  Skeleton,
  stagger,
  Workbench,
} from "../components/ui";

/**
 * Teams grouped by season, newest first, names alphabetical within a season.
 *
 * A coach accumulates a row per team per season, so an ungrouped list would
 * interleave three years of "River Bandits". Teams with no season sort last —
 * legacy coach-owned rows created before the season field existed lack one
 * and land in "Season not set", which exists to serve exactly that group.
 *
 * Called inline on every render rather than memoised: editing a team can move
 * it to another season, and a stale grouping would leave the card under its old
 * heading until something else re-rendered the page.
 */
function bySeason(teams: Team[]): { key: string; label: string; teams: Team[] }[] {
  const groups = new Map<string, { key: string; label: string; teams: Team[] }>();
  for (const team of teams) {
    const key = team.season ? seasonToValue(team.season) : "";
    const label = team.season ? formatSeason(team.season) : "Season not set";
    const group = groups.get(key) ?? { key, label, teams: [] };
    group.teams.push(team);
    groups.set(key, group);
  }
  return [...groups.values()]
    .sort((a, b) => {
      const sa = seasonFromValue(a.key);
      const sb = seasonFromValue(b.key);
      if (!sa) return 1;
      if (!sb) return -1;
      return compareSeasons(sb, sa); // descending: newest season first
    })
    .map((g) => ({ ...g, teams: [...g.teams].sort((x, y) => x.name.localeCompare(y.name)) }));
}

/** Shared by the create form and each card's edit form. */
function TeamForm({
  draft,
  setDraft,
  submitLabel,
  icon,
  pending,
  error,
  onSubmit,
  onCancel,
}: {
  draft: TeamDraft;
  setDraft: (next: TeamDraft) => void;
  submitLabel: string;
  icon?: ReactNode;
  pending: boolean;
  error?: string;
  onSubmit: () => void;
  onCancel?: () => void;
}) {
  // Derived once per render rather than per option: the list depends only on
  // today's date, so it never changes while the form is open.
  const seasons = seasonOptions(new Date());

  // A team filed under a season the picker no longer offers still has to show
  // its own value while being edited — otherwise the control renders blank and
  // the coach cannot tell what the team is currently set to.
  const current = seasonFromValue(draft.season);
  const seasonChoices =
    current && !seasons.some((s) => seasonToValue(s) === draft.season)
      ? [...seasons, current].sort((a, b) => compareSeasons(b, a))
      : seasons;

  const complete = draft.name.trim() && draft.ageGroup && draft.classification && draft.season;

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (complete) onSubmit();
      }}
    >
      <Field label="Team name">
        <Input
          placeholder="e.g. River Bandits"
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          required
        />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Age group">
          <Select
            value={draft.ageGroup}
            onChange={(e) => setDraft({ ...draft, ageGroup: e.target.value })}
            required
          >
            <option value="">Select…</option>
            {AGE_GROUPS.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Classification">
          <Select
            value={draft.classification}
            onChange={(e) => setDraft({ ...draft, classification: e.target.value })}
            required
          >
            <option value="">Select…</option>
            {CLASSIFICATIONS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <Field label="Season">
        <Select
          value={draft.season}
          onChange={(e) => setDraft({ ...draft, season: e.target.value })}
          required
        >
          <option value="">Select…</option>
          {seasonChoices.map((s) => (
            <option key={seasonToValue(s)} value={seasonToValue(s)}>
              {formatSeason(s)}
            </option>
          ))}
        </Select>
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="City (optional)">
          <Input
            value={draft.city}
            onChange={(e) => setDraft({ ...draft, city: e.target.value })}
          />
        </Field>
        <Field label="State (optional)">
          <Input
            value={draft.state}
            onChange={(e) => setDraft({ ...draft, state: e.target.value })}
          />
        </Field>
      </div>

      {error ? <p className="text-sm text-red-600">{error}</p> : null}

      <div className="flex gap-2">
        <Button type="submit" disabled={pending || !complete}>
          {icon}
          {pending ? "Saving…" : submitLabel}
        </Button>
        {onCancel ? (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
      </div>
    </form>
  );
}

/**
 * One roster card, and the edit form it becomes.
 *
 * Editing exists because the attribute backfill stamped every pre-existing team
 * with the same approximate default — without a way to correct it, a coach's
 * only recourse would be a replacement team, which orphans every entry pointing
 * at the old id and detaches the team's tournament history.
 */
function TeamCard({ team, index }: { team: Team; index: number }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => toDraft(team));
  const [error, setError] = useState<string>();

  const save = useMutation({
    mutationFn: () => api.put<Team>(`/me/teams/${team.id}`, toBody(draft)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["my-teams"] });
    },
  });

  // Re-seeded on every entry, not just on mount: the card may have been showing
  // a team the last save moved, and an abandoned edit must not persist.
  const startEdit = () => {
    setDraft(toDraft(team));
    setError(undefined);
    setEditing(true);
  };

  if (editing) {
    return (
      <Card className="animate-rise p-4" style={stagger(index)}>
        <TeamForm
          draft={draft}
          setDraft={setDraft}
          submitLabel="Save team"
          pending={save.isPending}
          error={error}
          onCancel={() => setEditing(false)}
          onSubmit={() =>
            save.mutate(undefined, {
              onSuccess: () => setEditing(false),
              onError: (e) => setError(apiErrorMessage(e, "Could not save this team.")),
            })
          }
        />
      </Card>
    );
  }

  // A pre-backfill team has no attributes yet and teamAttrs returns "" — guard
  // on the string so the line doesn't render as an empty styled span.
  const attrs = teamAttrs(team);
  return (
    <Card hover className="flex animate-rise items-center gap-3 p-4" style={stagger(index)}>
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 font-display text-sm font-bold text-brandText">
        {initials(team.name)}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium text-navy-950">{team.name}</span>
        {attrs ? (
          <span className="block truncate text-xs font-medium text-brandText">{attrs}</span>
        ) : null}
        {team.city || team.state ? (
          <span className="flex items-center gap-1 text-xs text-slate-500">
            <MapPinIcon className="h-3 w-3" />
            {[team.city, team.state].filter(Boolean).join(", ")}
          </span>
        ) : null}
      </span>
      {/* Named by the full label, not the bare name: two teams a coach owns can
          share a name and differ only by age group, and "Edit River Bandits"
          would be an ambiguous accessible name on both of them. */}
      <Button
        variant="ghost"
        size="sm"
        className="shrink-0"
        aria-label={`Edit ${teamLabel(team)}`}
        onClick={startEdit}
      >
        Edit
      </Button>
    </Card>
  );
}

/** My teams (spec §8.2): global teams the coach owns; create/list/edit. */
export function MyTeams() {
  const { idToken, ready } = useAuth();
  const qc = useQueryClient();
  const teamsQ = useQuery({
    queryKey: ["my-teams"],
    enabled: !!idToken,
    queryFn: () => api.get<{ teams: Team[] }>("/me/teams"),
  });

  const [draft, setDraft] = useState(emptyDraft);
  const [error, setError] = useState<string>();

  const create = useMutation({
    mutationFn: () => api.post<Team>("/me/teams", toBody(draft)),
    onSuccess: () => {
      trackTeamCreated();
      setDraft(emptyDraft());
      setError(undefined);
      void qc.invalidateQueries({ queryKey: ["my-teams"] });
    },
  });

  if (!ready) return <LoadingBlock />;
  if (!idToken) {
    return (
      <SignInCard
        title="Your teams"
        hint="Sign in to see your teams and create a new one."
        next="/me/teams"
      />
    );
  }

  const teams = teamsQ.data?.teams ?? [];
  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="My teams"
        title="Teams"
        subtitle="Your teams live across tournaments — register them for any open event."
      />

      <Workbench
        aside={
          <SectionCard
            title="Create a team"
            description="Age group, classification, and season identify the team. City and state are optional."
            className="animate-rise"
            style={stagger(2)}
          >
            <TeamForm
              draft={draft}
              setDraft={setDraft}
              submitLabel="Create team"
              icon={<PlusIcon className="h-4 w-4" />}
              pending={create.isPending}
              error={error}
              onSubmit={() =>
                create.mutate(undefined, {
                  onError: (e) => setError(apiErrorMessage(e, "Could not create this team.")),
                })
              }
            />
          </SectionCard>
        }
      >
        <Section
          title="Roster"
          badge={teams.length > 0 ? <Badge>{teams.length}</Badge> : undefined}
          className="animate-rise"
          style={stagger(1)}
        >
          {teamsQ.isLoading ? (
            <div className="grid gap-3 sm:grid-cols-2 2xl:grid-cols-3">
              <Skeleton className="h-20" />
              <Skeleton className="h-20" />
              <Skeleton className="h-20" />
            </div>
          ) : teams.length === 0 ? (
            <EmptyState title="No teams yet" hint="Name your first one under “Create a team”." />
          ) : (
            <div className="space-y-6">
              {bySeason(teams).map((group) => (
                <div key={group.key} className="space-y-3">
                  <h3 className="font-display text-sm font-semibold text-slate-500">
                    {group.label}
                  </h3>
                  <div className="grid gap-3 sm:grid-cols-2 2xl:grid-cols-3">
                    {group.teams.map((t, i) => (
                      <TeamCard key={t.id} team={t} index={i} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      </Workbench>
    </div>
  );
}
